import { useMediaQuery, useTheme } from "@mui/material";
import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  createContext,
  ReactNode,
  FC,
  useContext,
  useRef
} from "react";

type SidebarContextType = {
  isDrawOpen: boolean;
  setIsDrawOpen: (value: boolean) => void;
  toggleSidebar: () => void;
  /**
   * The caption of the nav group the current page sits in ("Analytics",
   * "Manage"). Published by the sidebar wrapper, which is the one place that
   * holds both the menu and the selected item, and read by the top bar for its
   * trailing crumb. Lives here rather than in a bar-specific context because
   * the wrapper and the bar are siblings under the layout, and this provider
   * is the shell state they already share.
   */
  sectionLabel: string;
  setSectionLabel: (value: string) => void;
};

export const SidebarContext = createContext<SidebarContextType>({
  isDrawOpen: true,
  sectionLabel: "",
  setIsDrawOpen: () => {},
  setSectionLabel: () => {},
  toggleSidebar: () => {}
});

type SidebarProviderProps = {
  children: ReactNode;
};

export const SidebarProvider: FC<SidebarProviderProps> = ({ children }) => {
  const theme = useTheme();
  // `noSsr`: without it the first render always reports "wide", so a tablet
  // paints the full nav and folds it a frame later.
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const [isDrawOpen, setIsDrawOpen] = useState(!isNarrow);
  const [sectionLabel, setSectionLabel] = useState("");

  const toggleSidebar = useCallback(() => {
    setIsDrawOpen((prev) => !prev);
  }, []);

  /**
   * The nav folds to its rail when the window gets narrow, and unfolds when it
   * gets wide again.
   *
   * At 264px the open nav is a third of a tablet's screen, and the page beside
   * it was down to about 450px — two insight cards, a truncated status column,
   * quick actions one to a row. The rail costs 62.
   *
   * It runs on the TRANSITION, not on the value, so it is a default and not a
   * lock: collapsing it yourself on a wide window, or opening it on a narrow
   * one, sticks until the window crosses the breakpoint again.
   */
  const wasNarrow = useRef(isNarrow);
  useEffect(() => {
    if (wasNarrow.current !== isNarrow) {
      wasNarrow.current = isNarrow;
      setIsDrawOpen(!isNarrow);
    }
  }, [isNarrow]);

  const value = useMemo(
    () => ({
      isDrawOpen,
      sectionLabel,
      setIsDrawOpen,
      setSectionLabel,
      toggleSidebar
    }),
    [isDrawOpen, sectionLabel, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};

export const useSidebarContext = () => useContext(SidebarContext);
